import {
  ContentfulShowCaseCard,
  ContentfulText,
  ContentfulTextIcon,
  ContentfulTextItem,
} from '@hellodarwin/core/lib/features/entities/checkout-entities';
import {
  createEntityAdapter,
  createSlice,
  EntityId,
  EntityState,
} from '@reduxjs/toolkit';
import { ContentfulClientApi, EntrySkeletonType, LocaleCode } from 'contentful';
import { RootState } from '../../../app/app-store';
import SliceRequest from '../slice-request';

const HD_PLATFORM_CMS_ID = '7l5xsOOS5a1ZzvSBJxlHVM';

const contentfulPlansAdapter = createEntityAdapter<
  ContentfulShowCaseCard,
  EntityId
>({
  selectId: (plan) => plan.planType,
  sortComparer: (a, b) => a.order - b.order,
});
interface CheckoutState {
  status: 'idle' | 'pending' | 'succeeded' | 'failed';
  error: string | null;
  contentfulPlans: EntityState<ContentfulShowCaseCard, EntityId>;
  contentfulText: ContentfulText | undefined;
}

// Initial state for the checkout slice
const initialState: CheckoutState = {
  status: 'idle',
  error: null,
  contentfulPlans: contentfulPlansAdapter.getInitialState(),
  contentfulText: undefined,
};

export const fetchContentfulPlans = SliceRequest<
  ContentfulShowCaseCard[],
  { contentfulClient: ContentfulClientApi<undefined>; locale: LocaleCode }
>('checkout/fetchContentfulPlans', async ({ contentfulClient, locale }) => {
  const response = await contentfulClient.getEntries<
    EntrySkeletonType<ContentfulShowCaseCard>,
    LocaleCode
  >({
    content_type: 'showCaseCard',
    locale,
  });
  return response.items.map((item) => item.fields);
});

export const fetchContentfulText = SliceRequest<
  ContentfulText,
  {
    contentfulClient: ContentfulClientApi<undefined>;
    locale: LocaleCode;
    entryId?: string;
  }
>(
  'checkout/fetchContentfulText',
  async ({ contentfulClient, locale, entryId = HD_PLATFORM_CMS_ID }) => {
    const entry = await contentfulClient.getEntry<
      EntrySkeletonType<ContentfulText>
    >(entryId, {
      include: 5,
      locale,
    });
    const fields = entry.fields;
    const items = (fields.items ?? []) as ContentfulTextItem[];
    const icons = items
      .map((listItem: ContentfulTextItem): ContentfulTextIcon | null => {
        const file = listItem?.fields?.icon?.fields?.icon?.fields?.file;
        if (!file?.fileName || !file?.url) return null;
        const fileUrl = file.url.startsWith('//')
          ? `https:${file.url}`
          : file.url;
        return {
          fileName: file.fileName,
          fileUrl,
        };
      })
      .filter((icon): icon is ContentfulTextIcon => !!icon);

    return {
      ...fields,
      icons,
    };
  },
);

// Create the checkout slice
export const checkoutSlice = createSlice({
  name: 'checkout',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },

    resetCheckout: () => {
      return initialState;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchContentfulPlans.pending, (state) => {
      state.status = 'pending';
    });
    builder.addCase(fetchContentfulPlans.fulfilled, (state, action) => {
      contentfulPlansAdapter.setAll(state.contentfulPlans, action.payload);
      state.status = 'succeeded';
    });
    builder.addCase(fetchContentfulPlans.rejected, (state) => {
      state.status = 'failed';
    });
    builder.addCase(fetchContentfulText.pending, (state) => {
      state.status = 'pending';
    });
    builder.addCase(fetchContentfulText.fulfilled, (state, action) => {
      state.contentfulText = action.payload;
      state.status = 'succeeded';
    });
    builder.addCase(fetchContentfulText.rejected, (state) => {
      state.status = 'failed';
    });
  },
});

// Export actions
export const { clearError, resetCheckout } = checkoutSlice.actions;

export const selectCheckoutError = (state: RootState) => state.checkout.error;
export const checkoutIsLoading = (state: RootState) =>
  state.checkout.status === 'pending';

export const {
  selectAll: selectAllContentfulPlans,
  selectEntities: selectContentfulPlans,
} = contentfulPlansAdapter.getSelectors<RootState>(
  (state) => state.checkout.contentfulPlans,
);

export const selectContentfulText = (state: RootState) =>
  state.checkout.contentfulText;

export default checkoutSlice.reducer;
